




































































































































































































































import { APIProvider } from '@/models/api/provider.model'
import { APIUser } from '@/models/api/user.model'
import { StatementNotificationService } from '@/services/modules/statement-notification.service'
import moment from 'moment'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

interface StatementRow {
    statementId: number;
    providerId: { providerId: number; providerName: string };
}

interface NotificationConfig {
    id: number;
    providerId: { providerId: number; providerName: string };
    emails: string;
    subject: string | null;
    body: string | null;
    deliveryAt: string | null;
}

interface TemplateConfig {
    subject: string;
    body: string;
}

interface AuthUser {
    providerId?: { providerId: number; providerName: string };
}

@Component
export default class StatementNotificationModal extends Vue {
    @Prop({ default: false }) value!: boolean
    @Prop({ required: true }) statement!: StatementRow

    formValid = false
    cloneFormValid = false
    loading = false
    loadingUsers = false
    saving = false
    sending = false

    notificationId: number | null = null
    deliveryAt: string | null = null
    template: TemplateConfig | null = null
    userEmailOptions: string[] = []
    selectedEmails: string[] = []
    droppedEmails: string[] = []

    form: { subject: string; body: string } = {
        subject: '',
        body: ''
    }

    // Clone state
    isCloning = false
    cloning = false
    loadingTargets = false
    loadingCloneUsers = false
    targetProviders: { text: string; value: number }[] = []
    cloneUserEmailOptions: string[] = []
    cloneEmails: string[] = []
    clone: { providerId: number | null } = { providerId: null }

    rules = {
        required: [(v: string) => !!v || 'This field is required'],
        provider: [(v: number) => !!v || 'Provider is required'],
        emails: [(v: string[]) => (v && v.length > 0) || 'At least one email is required']
    }

    get visible () { return this.value }
    set visible (val: boolean) { this.$emit('input', val) }

    get providerId (): number | null {
        return this.statement?.providerId?.providerId ?? null
    }

    get providerName (): string {
        return this.statement?.providerId?.providerName ?? '-'
    }

    /**
     * True while subject or body differ from the generic template, which is
     * exactly what the backend persists as a personalized value.
     */
    get isCustom (): boolean {
        if (!this.template) return !!(this.form.subject || this.form.body)
        return this.form.subject.trim() !== this.template.subject.trim() ||
            this.form.body.trim() !== this.template.body.trim()
    }

    @Watch('value')
    async onVisibleChange (val: boolean) {
        if (val) await this.load()
    }

    formatDate (date: string) {
        return moment(date).format('YYYY-MM-DD HH:mm')
    }

    async load () {
        this.reset()
        const providerId = this.providerId
        if (!providerId) return

        this.loading = true
        try {
            const [tplResp, resp] = await Promise.all([
                this.$store.dispatch('getStatementNotificationTemplate') as Promise<{
                    status: number; data: TemplateConfig | null;
                }>,
                this.$store.dispatch('getStatementNotificationByProvider', providerId) as Promise<{
                    status: number; data: NotificationConfig | null;
                }>
            ])
            await this.loadUsersByProvider(providerId)

            if (tplResp?.status === 200 && tplResp.data) this.template = tplResp.data

            // Subject and body default to the generic template and are only
            // overridden when this provider has personalized values stored.
            this.form = {
                subject: resp?.data?.subject || this.template?.subject || '',
                body: resp?.data?.body || this.template?.body || ''
            }

            if (resp?.status === 200 && resp.data) {
                this.notificationId = resp.data.id
                this.deliveryAt = resp.data.deliveryAt
                const saved = resp.data.emails
                    ? resp.data.emails.split(',').map(e => e.trim()).filter(Boolean)
                    : []
                // Stored emails whose user was disabled or deleted are no longer
                // offered by the provider users endpoint. They stay in the
                // database until this configuration is saved again, so warn
                // instead of dropping them silently.
                this.selectedEmails = saved.filter(e => this.userEmailOptions.includes(e))
                this.droppedEmails = saved.filter(e => !this.userEmailOptions.includes(e))
            }
        } finally {
            this.loading = false
        }
    }

    async loadUsersByProvider (providerId: number) {
        this.loadingUsers = true
        this.userEmailOptions = []
        try {
            const resp = await StatementNotificationService.getUsersByProvider(providerId)
            if (resp?.status === 200) {
                this.userEmailOptions = (resp.data as APIUser[])
                    .filter(u => u.email)
                    .map(u => u.email)
            }
        } finally {
            this.loadingUsers = false
        }
    }

    restoreTemplate () {
        if (!this.template) return
        this.form = { subject: this.template.subject, body: this.template.body }
    }

    validate () {
        return (this.$refs.form as Vue & { validate: () => boolean }).validate()
    }

    /**
     * Creates the config the first time and updates it afterwards.
     * Returns true when it was persisted.
     */
    async persist (): Promise<boolean> {
        const providerId = this.providerId
        if (!providerId) return false

        const params = {
            emails: this.selectedEmails.join(','),
            subject: this.form.subject,
            body: this.form.body
        }
        const resp = await (this.notificationId
            ? this.$store.dispatch('updateStatementNotification', {
                id: this.notificationId, params
            })
            : this.$store.dispatch('createStatementNotification', { providerId, ...params })
        ) as { status: number; data?: NotificationConfig }

        if (resp?.status === 200) {
            if (resp.data?.id) this.notificationId = resp.data.id
            // Only the active selection was persisted, so the inactive ones
            // are gone from the database and no longer need warning about.
            this.droppedEmails = []
            return true
        }
        this.$store.commit('reportError', {
            code: resp?.status,
            message: 'Error saving notification'
        })
        return false
    }

    async save () {
        if (!this.validate()) return
        this.saving = true
        const ok = await this.persist()
        this.saving = false
        if (ok) {
            this.$store.commit('reportSuccess', 'Done Successfully!')
            this.close()
        }
    }

    async send () {
        if (!this.validate()) return
        this.sending = true
        const ok = await this.persist()
        if (!ok) {
            this.sending = false
            return
        }

        const resp = await this.$store.dispatch(
            'sendStatementNotification', this.statement.statementId
        ) as { status: number; data?: { message?: string } }
        this.sending = false

        if (resp?.status === 200) {
            this.$store.commit('reportSuccess', 'Statement sent successfully!')
            this.close()
        } else {
            this.$store.commit('reportError', {
                code: resp?.status,
                message: resp?.data?.message || 'Error sending statement'
            })
        }
    }

    // CLONE

    async openClone () {
        this.resetClone()
        this.isCloning = true
        this.loadingTargets = true
        try {
            const resp = await this.$store.dispatch('getStatementNotifications') as {
                status: number; data: NotificationConfig[];
            }
            const configured = resp?.status === 200 ? resp.data : []
            const configuredIds = new Set(
                configured.map(c => c.providerId?.providerId ?? (c.providerId as unknown as number))
            )

            // Only providers without a configuration can receive the clone,
            // since a provider holds a single notification.
            await this.$store.dispatch('getProviders')
            const user = this.$store.state.AuthStore.auth.user as AuthUser
            const all: { text: string; value: number }[] = user.providerId?.providerId
                ? [{ text: user.providerId.providerName, value: user.providerId.providerId }]
                : (this.$store.state.ProvidersStore?.providers || []).map((p: APIProvider) => ({
                    text: p.providerName,
                    value: p.providerId
                }))
            this.targetProviders = all.filter(p => !configuredIds.has(p.value))
        } finally {
            this.loadingTargets = false
        }
    }

    async onCloneProviderChange (providerId: number) {
        this.cloneEmails = []
        this.cloneUserEmailOptions = []
        if (!providerId) return
        this.loadingCloneUsers = true
        try {
            const resp = await StatementNotificationService.getUsersByProvider(providerId)
            if (resp?.status === 200) {
                this.cloneUserEmailOptions = (resp.data as APIUser[])
                    .filter(u => u.email)
                    .map(u => u.email)
            }
        } finally {
            this.loadingCloneUsers = false
        }
    }

    async confirmClone () {
        const valid = (this.$refs.cloneForm as Vue & { validate: () => boolean }).validate()
        if (!valid) return

        this.cloning = true
        // The values on screen are sent as-is: the backend stores them as null
        // when they still match the generic template, so an inherited source
        // stays inherited and a personalized one keeps its own texts.
        const resp = await this.$store.dispatch('createStatementNotification', {
            providerId: this.clone.providerId,
            emails: this.cloneEmails.join(','),
            subject: this.form.subject,
            body: this.form.body
        }) as { status: number; data?: { message?: string } }
        this.cloning = false

        if (resp?.status === 200) {
            this.$store.commit('reportSuccess', 'Notification cloned successfully!')
            this.cancelClone()
        } else if (resp?.status === 409) {
            this.$store.commit('reportError', {
                code: 409,
                message: 'This provider already has a notification configured'
            })
        } else {
            this.$store.commit('reportError', {
                code: resp?.status,
                message: 'Error cloning notification'
            })
        }
    }

    cancelClone () {
        this.resetClone()
        this.isCloning = false
    }

    resetClone () {
        this.clone = { providerId: null }
        this.cloneEmails = []
        this.cloneUserEmailOptions = []
        this.targetProviders = []
        if (this.$refs.cloneForm) {
            (this.$refs.cloneForm as Vue & { resetValidation: () => void }).resetValidation()
        }
    }

    reset () {
        this.notificationId = null
        this.deliveryAt = null
        this.template = null
        this.form = { subject: '', body: '' }
        this.selectedEmails = []
        this.userEmailOptions = []
        this.droppedEmails = []
        this.isCloning = false
        this.resetClone()
        if (this.$refs.form) {
            (this.$refs.form as Vue & { resetValidation: () => void }).resetValidation()
        }
    }

    close () {
        this.reset()
        this.$emit('input', false)
    }
}
